/**
 * Vision AI Model Interfaces
 * These interfaces match the C# backend models for Vision AI functionality
 */

// =============================================================================
// ENUMS (matching backend EVision* enums)
// =============================================================================

/**
 * Vision template types/categories
 * Corresponds to EVisionTemplateTypes enum in backend
 */
export enum EVisionTemplateTypes {
  Custom = "Custom",
  Factory = "Factory",
  Hospital = "Hospital",
  Warehouse = "Warehouse",
  Cafe = "Cafe",
  Restaurant = "Restaurant",
  Office = "Office"
}

/**
 * Node types for vision workflow
 * Corresponds to EVisionNodeTypes enum in backend
 * Uses camelCase to match backend EnumMember values
 */
export enum EVisionNodeTypes {
  StartNode = "startNode",
  VideoInput = "videoInput",
  Detector = "detector",
  Tracker = "tracker",
  Segmentation = "segmentation",
  AnalyticsProcessor = "analyticsProcessor",
  MetricsCalculator = "metricsCalculator",
  DashboardOutput = "dashboardOutput",
  ReportGenerator = "reportGenerator",
  DatabaseService = "databaseService",
  ApiGateway = "apiGateway"
}

/**
 * Video input types
 * Corresponds to EVisionVideoInputTypes enum in backend
 */
export enum EVisionVideoInputTypes {
  Rtsp = "rtsp",
  Webrtc = "webrtc",
  FileUrl = "fileUrl",
  FilePath = "filePath"
}

/**
 * Database types for DatabaseService node
 * Corresponds to EVisionDatabaseTypes enum in backend
 */
export enum EVisionDatabaseTypes {
  PostgreSql = "postgresql",
  MySql = "mysql",
  Mssql = "mssql",
  MongoDb = "mongodb",
  Redis = "redis"
}

/**
 * Authentication types for API Gateway
 * Corresponds to EVisionAuthType enum in backend
 */
export enum EVisionAuthType {
  None = "none",
  Basic = "basic",
  OAuth = "oauth"
}

/**
 * Window/Schedule types for MetricsCalculator and ReportGenerator
 * Corresponds to EVisionMetricTypes enum in backend
 */
export enum EVisionMetricTypes {
  Manual = "manual",
  Realtime = "realtime",
  Hourly = "hourly",
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly"
}

/**
 * Aggregation types for metrics
 * Corresponds to EVisionAggregationTypes enum in backend
 */
export enum EVisionAggregationTypes {
  Count = "count",
  Average = "average",
  Sum = "sum",
  Min = "min",
  Max = "max"
}

/**
 * Output types for nodes
 * Corresponds to EVisionOutputTypes enum in backend
 */
export enum EVisionOutputTypes {
  Dashboard = "dashboard",
  Webhook = "webhook",
  Database = "database"
}

/**
 * Report types
 * Corresponds to EVisionReportTypes enum in backend
 */
export enum EVisionReportTypes {
  Pdf = "pdf",
  Excel = "excel",
  Json = "json"
}

/**
 * Widget types for DashboardOutput
 * Corresponds to EVisionWidgetTypes enum in backend
 */
export enum EVisionWidgetTypes {
  Chart = "chart",
  Gauge = "gauge",
  Alert = "alert",
  Counter = "counter",
  Table = "table",
  Video = "video"
}

// =============================================================================
// NODE DATA CONFIG INTERFACES
// =============================================================================

/**
 * Configuration for VideoInput node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IVideoInputConfig {
  videoSource?: string;
  inputType?: EVisionVideoInputTypes;
  previewSource?: string;
  width?: number;
  height?: number;
  fps?: number;
}

/**
 * Configuration for Detector node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IDetectorConfig {
  model?: string;
  confidence?: number;
  classes?: string;
  customClasses?: string;
  device?: "cpu" | "cuda";
}

/**
 * Configuration for Tracker node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface ITrackerConfig {
  model?: string;
  maxAge?: number;
  device?: "cpu" | "cuda";
}

/**
 * Configuration for Segmentation node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface ISegmentationConfig {
  model?: string;
  fillMask?: boolean;
  device?: "cpu" | "cuda";
}

/**
 * Zone definition for AnalyticsProcessor
 * Corresponds to VisionZoneModel in backend
 */
export interface IVisionZone {
  id: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color?: string;
  triggers?: string[];
}

/**
 * Configuration for AnalyticsProcessor node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IAnalyticsProcessorConfig {
  zones?: IVisionZone[];
  eventTypes?: string[];
  confidenceThreshold?: number;
  cooldownMs?: number;
  previewSampleUrl?: string;
}

/**
 * Configuration for MetricsCalculator node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IMetricsCalculatorConfig {
  windowType?: EVisionMetricTypes;
  windowSize?: number;
  metrics?: string[];
  aggregationType?: EVisionAggregationTypes;
}

/**
 * Widget definition for DashboardOutput
 * Corresponds to VisionWidgetModel in backend
 */
export interface IDashboardWidget {
  id: string;
  type: EVisionWidgetTypes;
  metric: string;
  title: string;
  refreshInterval?: number;
}

/**
 * Configuration for DashboardOutput node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IDashboardOutputConfig {
  widgets?: IDashboardWidget[];
  websocketUrl?: string;
  enableThumbnails?: boolean;
}

/**
 * Configuration for ReportGenerator node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IReportGeneratorConfig {
  reportType?: EVisionReportTypes;
  schedule?: EVisionMetricTypes;
  recipients?: string[];
  includeCharts?: boolean;
  includeHistoricalData?: boolean;
}

/**
 * Retention policy for database
 * Corresponds to RetentionPolicyModel in backend
 */
export interface IRetentionPolicy {
  events?: number;
  metrics?: number;
  reports?: number;
}

/**
 * Configuration for DatabaseService node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IDatabaseServiceConfig {
  connectionString?: string;
  databaseType?: EVisionDatabaseTypes;
  tables?: string[];
  retentionPolicy?: IRetentionPolicy;
}

/**
 * API endpoint definition
 * Corresponds to VisionEndpointModel in backend
 */
export interface IApiEndpoint {
  path: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  dataSource?: string;
}

/**
 * Configuration for ApiGateway node
 * Corresponds to part of VisionDataConfigModel in backend
 */
export interface IApiGatewayConfig {
  endpoints?: IApiEndpoint[];
  websocketEnabled?: boolean;
  corsEnabled?: boolean;
  authentication?: EVisionAuthType;
}

/**
 * Union type for all node configurations
 */
export type IVisionDataConfig =
  | IVideoInputConfig
  | IDetectorConfig
  | ITrackerConfig
  | ISegmentationConfig
  | IAnalyticsProcessorConfig
  | IMetricsCalculatorConfig
  | IDashboardOutputConfig
  | IReportGeneratorConfig
  | IDatabaseServiceConfig
  | IApiGatewayConfig;

// =============================================================================
// NODE AND WORKFLOW INTERFACES
// =============================================================================

/**
 * Position of a node in the workflow canvas
 */
export interface IVisionNodePosition {
  x: number;
  y: number;
}

/**
 * Vision workflow node
 * Corresponds to VisionNodeModel in backend
 */
export interface IVisionNode {
  id: string;
  type: EVisionNodeTypes | string;
  displayName?: string;
  position: IVisionNodePosition;
  width?: number;
  height?: number;
  data: {
    label?: string;
    title?: string;
    type?: string;
    config?: IVisionDataConfig;
  };
}

/**
 * Vision workflow edge (connection between nodes)
 * Corresponds to VisionEdgeModel in backend
 */
export interface IVisionEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

/**
 * Parsed workflow data from VisionTemplate.visionModel JSON string
 * Corresponds to VisionInfoModel in backend
 */
export interface IVisionWorkflowData {
  id: string;
  title: string;
  description: string;
  type: EVisionTemplateTypes | string;
  nodes: IVisionNode[];
  edges: IVisionEdge[];
}

// =============================================================================
// TEMPLATE AND MODEL INTERFACES
// =============================================================================

/**
 * Represents a neural network model for vision tasks (object detection, tracking, segmentation).
 * Corresponds to VisionInferenceModel.cs in backend.
 */
export interface IVisionInferenceModel {
  id: string;
  name: string;
  type: "ObjectDetection" | "Tracking" | "Segmentation";
  framework: string;
  size?: string;
  inputResolution?: string;
  avgFps?: number;
  description?: string;
  /** Recommended default confidence threshold (0-100) for this model */
  defaultConfidence?: number;
}

/**
 * Represents a pre-built workflow template for Vision AI actions
 * Corresponds to VisionTemplate.cs in backend
 */
export interface IVisionTemplate {
  id: string;
  name: string;
  description: string;
  shortDescription?: string;
  tags?: string[];
  imageUrl?: string;
  visionModel: string;
}

/**
 * Helper function to parse template visionModel JSON
 * Converts backend format to ReactFlow-compatible format
 *
 * Supports both formats for backwards compatibility:
 * - Nested format (dbJSON2.json): node.data.config contains the configuration
 * - Legacy flat format: node.data contains config properties directly
 */
export function parseVisionTemplate(template: IVisionTemplate): IVisionWorkflowData {
  const workflowData: IVisionWorkflowData = template.visionModel
    ? JSON.parse(template.visionModel)
    : { id: "", title: "", description: "", type: EVisionTemplateTypes.Custom, nodes: [], edges: [] };

  return normalizeVisionNodes(workflowData);
}

/**
 * Helper function to parse visionInfo JSON string from saved actions
 * Normalizes node structure to ensure data.config format is used
 */
export function parseVisionInfo(visionInfoJson: string): { nodes: any[]; edges: any[]; type?: string } {
  const parsed = JSON.parse(visionInfoJson);
  const nodes = parsed.nodes || [];
  const edges = parsed.edges || [];

  // Normalize nodes to ensure they have the proper structure
  const normalizedNodes = nodes.map((node: any) => {
    const nodeType = node.type as string;

    // Check if already in nested format (has data.config)
    if (node.data?.config) {
      // Already nested - just ensure title/label are set
      const displayTitle = node.data.title || node.displayName || getDefaultNodeTitle(nodeType);
      return {
        ...node,
        type: nodeType,
        data: {
          ...node.data,
          title: displayTitle,
          label: displayTitle
        }
      };
    }

    // Legacy flat format - convert to nested
    const { title, label, type: dataType, ...configData } = node.data || {};
    const displayTitle = node.displayName || title || label || getDefaultNodeTitle(nodeType);

    return {
      ...node,
      type: nodeType,
      data: {
        title: displayTitle,
        label: displayTitle,
        // For processor nodes (detector, tracker, segmentation), include the type in data
        ...(nodeType === EVisionNodeTypes.Detector ||
        nodeType === EVisionNodeTypes.Tracker ||
        nodeType === EVisionNodeTypes.Segmentation
          ? { type: nodeType }
          : {}),
        // Move all config properties into data.config for node components to consume
        config: configData
      }
    };
  });

  return {
    nodes: normalizedNodes,
    edges,
    type: parsed.type
  };
}

/**
 * Internal helper to normalize nodes in workflow data
 */
function normalizeVisionNodes(workflowData: IVisionWorkflowData): IVisionWorkflowData {
  // Restructure data to match what frontend node components expect
  // Here node:any because we are transforming the structure to add data.config and fit IVisionNode interface
  workflowData.nodes = workflowData.nodes.map((node: any) => {
    const nodeType = node.type as string;

    // Check if already in nested format (has data.config)
    if (node.data?.config) {
      // Already nested - just ensure title/label are set
      const displayTitle = node.data.title || node.displayName || getDefaultNodeTitle(nodeType);
      return {
        ...node,
        type: nodeType,
        width: node.width,
        height: node.height,
        data: {
          ...node.data,
          title: displayTitle,
          label: displayTitle
        }
      };
    }

    // Legacy flat format - convert to nested
    const { title, label, type: dataType, ...configData } = node.data || {};
    const displayTitle = node.displayName || title || label || getDefaultNodeTitle(nodeType);

    return {
      ...node,
      type: nodeType,
      width: node.width,
      height: node.height,
      data: {
        title: displayTitle,
        label: displayTitle,
        // For processor nodes (detector, tracker, segmentation), include the type in data
        ...(nodeType === EVisionNodeTypes.Detector ||
        nodeType === EVisionNodeTypes.Tracker ||
        nodeType === EVisionNodeTypes.Segmentation
          ? { type: nodeType }
          : {}),
        // Move all config properties into data.config for node components to consume
        config: configData
      }
    };
  });

  return workflowData;
}

/**
 * Get default display title for a node type
 */
export function getDefaultNodeTitle(nodeType: string): string {
  const titles: Record<string, string> = {
    startNode: "Start",
    videoInput: "Video Input",
    detector: "Detector",
    tracker: "Tracker",
    segmentation: "Segmentation",
    analyticsProcessor: "Analytics Processor",
    metricsCalculator: "Metrics Calculator",
    dashboardOutput: "Dashboard Output",
    reportGenerator: "Report Generator",
    databaseService: "Database Service",
    apiGateway: "API Gateway"
  };
  return titles[nodeType] || "Node";
}

/**
 * Helper function to serialize workflow data for backend
 * Keeps the nested format with data.config structure
 * Node types are in camelCase (matching backend EnumMember values)
 */
export function serializeVisionWorkflow(workflowData: IVisionWorkflowData): string {
  const backendData = {
    ...workflowData,
    nodes: workflowData.nodes.map((node) => {
      return {
        id: node.id,
        type: node.type,
        displayName: node.data?.title || node.data?.label || getDefaultNodeTitle(node.type as string),
        position: node.position,
        width: (node as any).width,
        height: (node as any).height,
        data: node.data // Keep nested structure as-is
      };
    })
  };
  return JSON.stringify(backendData);
}
